/* Market Intelligence — a faithful React port of the prototype's rIntel screen.
 *
 * Know your enemy and your customer. Four read-only intelligence views:
 *   1. Competitive landscape — your active products and each AI rival, with
 *      price, brand, estimated share, key components and (for rivals) a teardown
 *      of completed research and the branch they're likely researching next.
 *   2. Segment-share forecast — estimated share of each segment's demand if the
 *      quarter ran today, your generations vs every rival.
 *   3. Demand drivers — the additive Overall Rating decomposition (quality +
 *      brand + price points, weighted by each segment's concern split) × the
 *      distribution reach, for any product or rival.
 *   4. Market-share trend over the played quarters.
 *
 * Pure analysis: nothing is staged here. Computed from buildWorkingGame so the
 * forecasts reflect the design / pricing / marketing / distribution edits staged
 * on the other screens. Sub-sections are plain render functions so the page never
 * scroll-jumps. (The decorative 3D product snapshots are omitted.) */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMemo, useState } from 'react';
import { Engine, applyGameCfg, SEGMENTS, COMPETITORS, TECH_BY_ID, TECH_BRANCHES, QUARTER_NAMES, fmt$, fmtK, clamp } from '@venturewrist/engine';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from 'recharts';
import { useReduceMotion } from '../../hooks/useReduceMotion';
import { useDecisions } from '../../store/decisions';
import { buildWorkingGame } from '../../lib/designState';
import { EmptyNote } from '../../components/ui';
import type { ScreenProps } from './types';
import '../../styles/intel.css';

const Eng = Engine as any;
const SEGS = SEGMENTS as any[];
const BY_ID = TECH_BY_ID as Record<string, any>;
const BRANCHES = TECH_BRANCHES as any[];
const COMPALL = COMPETITORS as any[];

const TOOLTIP = { background: 'var(--panel)', border: '1px solid var(--card-b)', borderRadius: 10, color: 'var(--txt)' } as const;
const WhyChip = () => <span className="tooltip-why" title="Learn the theory">?</span>;
const Bar = ({ v }: { v: number }) => (
  <div className="bar-track"><div className="bar-fill" style={{ width: clamp(v, 0, 100) + '%' }} /></div>
);
const concernTag = (seg: any) => `P${seg.concerns.pc}·Q${seg.concerns.qc}·B${seg.concerns.bc}`;
function SegTag({ segId }: { segId: string }) {
  const s = SEGS.find((x) => x.id === segId);
  if (!s) return null;
  return <span className="seg-tag" style={{ background: s.color + '22', color: s.color }}>{s.icon} {s.name}</span>;
}
const fmtPts = (v: number) => (v >= 0 ? '+' : '−') + Math.abs(v).toFixed(0);

export function Intel({ detail }: ScreenProps) {
  const reduceMotion = useReduceMotion();
  const draft = useDecisions((z) => z.draft);
  const [legacy, setLegacy] = useState(false);
  const [ddGen, setDdGen] = useState<Record<string, number>>({});
  const [ddSeg, setDdSeg] = useState<Record<string, string>>({});
  const [ddCompSeg, setDdCompSeg] = useState<Record<string, string>>({});

  const g = useMemo(() => buildWorkingGame(detail.state, draft), [detail.state, draft]);
  applyGameCfg(g);

  const round = g.round as number;
  const last = (g.history ?? []).at(-1);
  const models = Eng.activeModels(g) as any[];
  const nComps = g.numComps ? Math.max(1, Math.min(g.numComps, COMPALL.length)) : 3;
  const comps = COMPALL.slice(0, nComps);
  const compGenN = Eng.compGenNum(round);

  // ── competitive landscape ──────────────────────────────────────────────────────
  const landscape = () => (
    <div className="card" style={{ marginBottom: 14 }}>
      <h3>Competitive landscape — you vs rivals</h3>
      <div className="grid3">
        {models.map((m) => {
          const cur = Eng.currentGen(m), seg = SEGS.find((s) => s.id === m.targetSeg);
          const devG = Eng.devGen(m), spec = cur.spec;
          const comp = (cat: string, id: string) => Eng.comp(cat, id)?.name ?? id;
          return (
            <div key={m.id} className="card" style={{ background: 'rgb(var(--c-acc) / .05)', borderColor: 'rgb(var(--c-acc) / .35)' }}>
              <div className="competitor-card">
                <div>
                  <b style={{ color: 'var(--acc)' }}>{Eng.genName(m, cur)}</b> <span className="chip acc" style={{ fontSize: 9.5, padding: '2px 7px' }}>YOU</span>
                  <div className="sub">targets {seg.icon} {seg.name}</div>
                  <div style={{ marginTop: 6, fontSize: 12.5 }}>Price <b>{fmt$(cur.price)}</b> · Brand <b>{Math.round(Eng.loyaltyOf(g, m))}</b></div>
                  {last ? <div className="sub">est. share {(last.share * 100).toFixed(1)}% (all your products)</div> : null}
                </div>
              </div>
              <div className="sub" style={{ marginTop: 8 }}>
                {[comp('cpu', spec.cpu), comp('display', spec.display), comp('casing', spec.casing)].join(' · ')} · {devG ? 'next gen launches ' + QUARTER_NAMES(devG.committed + 1) : 'no next gen in engineering'}
              </div>
            </div>
          );
        })}
        {comps.map((c) => {
          const st = Eng.competitorState(c, round, g);
          const share = last ? last.compShare?.[c.id] : null;
          const rdone = Eng.rivalDone(g, c.id) as string[];
          const espec = Eng.rivalSpec(c, rdone);
          const comp = (cat: string, id: string) => Eng.comp(cat, id)?.name ?? id;
          const nextGenIn = 3 - ((round - 1) % 3);
          const rt = (g.rivalTech || {})[c.id];
          const curBr = rt && rt.active ? BRANCHES.find((b) => b.id === BY_ID[rt.active].branch) : null;
          return (
            <div key={c.id} className="card" style={{ background: 'var(--wash)' }}>
              <div className="competitor-card">
                <div>
                  <b style={{ color: c.color }}>{c.name} {c.model || ''} {compGenN}</b>
                  <div className="sub">{c.tag}</div>
                  <div style={{ marginTop: 6, fontSize: 12.5 }}>Price <b>{fmt$(st.price)}</b> · Brand <b>{Math.round(st.brand)}</b></div>
                  {share != null ? <div className="sub">est. share {(share * 100).toFixed(1)}%</div> : null}
                </div>
              </div>
              <div className="sub" style={{ marginTop: 8 }}>
                Focus: {c.focus.map((s: string) => SEGS.find((x) => x.id === s)?.icon).join(' ')} · {[comp('cpu', espec.cpu), comp('display', espec.display), comp('casing', espec.casing)].join(' · ')} · next gen in {nextGenIn} qtr{nextGenIn === 1 ? '' : 's'}
              </div>
              <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
                {rdone.length
                  ? rdone.slice(-6).map((id) => <span key={id} className="chip" style={{ fontSize: 9, padding: '1px 6px' }} title={BY_ID[id].name}>{BY_ID[id].icon} {BY_ID[id].name}</span>)
                  : <span className="sub" style={{ fontSize: 11 }}>no completed research yet</span>}
              </div>
              {curBr ? <div className="sub" style={{ marginTop: 5, fontSize: 11 }}>researching: likely {curBr.icon} {curBr.name} branch</div> : null}
            </div>
          );
        })}
      </div>
    </div>
  );

  // ── segment-share forecast ───────────────────────────────────────────────────
  const forecast = () => {
    const cols: { m: any; gv: any }[] = [];
    for (const m of models) {
      const cur = Eng.currentGen(m);
      cols.push({ m, gv: cur });
      if (legacy) for (const gv of (Eng.prodGens(m) as any[]).slice().sort((a, b) => b.gen - a.gen)) if (gv !== cur) cols.push({ m, gv });
    }
    const hasLegacy = models.some((m) => (Eng.prodGens(m) as any[]).length > 1);
    const segShares = Eng.segSharesEst(g, cols, round);
    return (
      <div className="card" style={{ marginBottom: 14 }}>
        <div className="spread">
          <h3 style={{ marginBottom: 12 }}>Segment share forecast — your products vs rivals</h3>
          {hasLegacy ? <button className={'opt-pill' + (legacy ? ' on' : '')} style={{ fontSize: 11 }} onClick={() => setLegacy((v) => !v)}>{legacy ? '− legacy' : '+ legacy'}</button> : null}
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table className="tbl">
            <thead>
              <tr>
                <th>Segment</th><th className="num">Mkt size/qtr</th>
                {cols.map(({ m, gv }, i) => <th key={i} className="num">{Eng.genStatus(m, gv) === 'legacy' ? '◐ ' : ''}{Eng.genName(m, gv)}</th>)}
                {comps.map((c) => <th key={c.id} className="num" style={{ color: c.color }}>{(c.model || c.name)} {compGenN}</th>)}
              </tr>
            </thead>
            <tbody>
              {SEGS.map((seg) => {
                const size = Eng.segWorldDemand(seg.id, round);
                const o = segShares[seg.id];
                const pct = (u: number) => Math.round(u / (o.D || 1) * 100);
                return (
                  <tr key={seg.id}>
                    <td>{seg.icon} {seg.name}<div className="sub" title={`Concern weights: Price ${seg.concerns.pc}% · Quality ${seg.concerns.qc}% · Brand ${seg.concerns.bc}%`}>{concernTag(seg)} · ref {fmt$(seg.refPrice)}</div></td>
                    <td className="num">{fmtK(size)}</td>
                    {o.mine.map((u: number, i: number) => { const v = pct(u); return <td key={i} className="num"><b style={{ color: v >= 25 ? 'var(--good)' : v >= 10 ? 'var(--warn)' : 'var(--mut)' }}>{v}%</b></td>; })}
                    {o.comps.map((u: number, i: number) => <td key={i} className="num" style={{ color: 'var(--mut)' }}>{pct(u)}%</td>)}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="sub" style={{ marginTop: 8 }}>Estimated share of each segment's demand if the quarter ran today: every product gets an <b>Overall Rating</b> (quality + brand + price points, weighted by the segment's concerns — P/Q/B shown per row), then ratings compete for the buyers each product's distribution can reach. Demand-weighted across all countries in play — the same math the quarterly engine uses. Each row sums to ≈100%.</div>
      </div>
    );
  };

  // ── demand-driver decomposition ────────────────────────────────────────────────
  const concernBar = (seg: any) => {
    const cc = seg.concerns;
    const cell = (w: number, color: string, label: string, title: string) => (
      <div style={{ width: w + '%', background: color }} title={title}>{w >= 15 ? label : String(w)}</div>
    );
    return (
      <>
        <div className="sub" style={{ marginBottom: 3 }}>What {seg.name} weigh when buying (concern weights):</div>
        <div style={{ display: 'flex', height: 15, borderRadius: 8, overflow: 'hidden', fontSize: 9.5, lineHeight: '15px', textAlign: 'center', marginBottom: 11, color: 'var(--on-accent)', fontWeight: 700 }}>
          {cell(cc.pc, 'var(--good)', '💲 Price ' + cc.pc + '%', 'Price Concern ' + cc.pc + '%')}
          {cell(cc.qc, 'var(--acc)', '⚙️ Quality ' + cc.qc + '%', 'Quality Concern ' + cc.qc + '%')}
          {cell(cc.bc, 'var(--acc2)', '🏷️ Brand ' + cc.bc + '%', 'Brand Concern ' + cc.bc + '%')}
        </div>
      </>
    );
  };
  const ddRows = (b: any, seg: any) => {
    const cc = seg.concerns;
    const q = b.qualityPts, br = b.brandPts, p = b.pricePts, R = Math.max(q + br + p, 1);
    const maxR = 100 * cc.qc / 60 + 100 * cc.bc / 60 + cc.pc;
    const pPct = Math.abs(b.priceTerm * 100).toFixed(0);
    const rows: [any, string, any, number][] = [
      ['⚙️ Quality points', `${Math.round(b.fit)} (Segment Fit) × ${cc.qc} (Quality Concern) ÷ 60 = ${fmtPts(q)}`, fmtPts(q), (q / (cc.qc * 100 / 60)) * 100],
      ['🏷️ Brand points', `${Math.round(b.brandR)} (Brand Rating) × ${cc.bc} (Brand Concern) ÷ 60 = ${fmtPts(br)}`, fmtPts(br), (br / (cc.bc * 100 / 60)) * 100],
      ['💲 Price points', `${pPct}% ${b.priceTerm >= 0 ? 'below' : 'above'} reference price × ${cc.pc} (Price Concern) = ${fmtPts(p)}`, fmtPts(p), clamp((p + cc.pc) / (2 * cc.pc) * 100, 0, 100)],
      [<b>= Overall Rating</b>, `${fmtPts(q)} (Quality) ${fmtPts(br)} (Brand) ${fmtPts(p)} (Price) = ${Math.round(R)}`, <><span style={{ color: 'var(--acc)' }}>{Math.round(R)}</span><span className="sub"> / max {Math.round(maxR)}</span></>, clamp(R / maxR * 100, 0, 100)],
      ['🏪 Distribution reach', "share of this segment's buyers your channels can reach", '×' + b.coverage.toFixed(2), b.coverage * 100],
    ];
    return rows.map(([l, calc, v, pctVal], i) => (
      <div key={i} style={{ marginBottom: 8 }}>
        <div className="spread" style={{ fontSize: 11.5, marginBottom: 1 }}><span>{l}</span><b>{v}</b></div>
        <div className="sub" style={{ fontSize: 10.5, marginBottom: 2, fontFamily: 'monospace' }}>{calc}</div>
        <Bar v={pctVal} />
      </div>
    ));
  };

  const demandDrivers = () => (
    <div className="card" style={{ marginBottom: 14 }}>
      <h3>Demand drivers — how every product's Overall Rating is calculated<WhyChip /></h3>
      <div className="sub" style={{ marginBottom: 6 }}>Buyers score every product — yours and your rivals' — with an <b>Overall Rating: quality, brand and price points added together</b>, so strength in one factor can pay for weakness in another. Each segment splits 100 concern points between Price, Quality and Brand. <b>Quality is your design fit</b>; <b>Brand Rating = 0.6 × loyalty + 40 × awareness</b>. Ratings then compete for the buyers each product's distribution can reach.</div>
      <div className="sub" style={{ marginBottom: 12, fontFamily: 'monospace', fontSize: 11, opacity: 0.85 }}>Overall Rating = Fit × Quality Concern ⁄ 60 &nbsp;+&nbsp; Brand Rating × Brand Concern ⁄ 60 &nbsp;+&nbsp; (RefPrice·income − Price) ⁄ (RefPrice·income) × Price Concern</div>
      <div className="grid2">
        {models.map((m) => {
          const liveG = (Eng.liveGens(m) as any[]).slice().sort((a, b) => b.gen - a.gen);
          if (!liveG.length) return null;
          let gvSel = liveG.find((x) => x.gen === ddGen[m.id]) || Eng.currentGen(m);
          if (!liveG.includes(gvSel)) gvSel = liveG[0];
          const segSel = ddSeg[m.id] || m.targetSeg;
          const b = Eng.demandBreakdown(g, m, gvSel, segSel);
          const segObj = SEGS.find((s) => s.id === segSel);
          return (
            <div key={m.id} style={{ padding: 13, border: '1px solid var(--card-b)', borderRadius: 11, background: 'var(--field)' }}>
              <div className="spread" style={{ marginBottom: 8 }}><b>{m.name}</b><SegTag segId={m.targetSeg} /></div>
              {liveG.length > 1 ? (
                <div className="opt-row" style={{ marginBottom: 8 }}>
                  {liveG.map((gv) => <button key={gv.gen} className={'opt-pill' + (gv === gvSel ? ' on' : '')} style={{ padding: '4px 9px', fontSize: 11 }} onClick={() => setDdGen((p) => ({ ...p, [m.id]: gv.gen }))}>{Eng.STATUS[Eng.genStatus(m, gv)].icon} Gen {gv.gen}</button>)}
                </div>
              ) : null}
              <div className="opt-row" style={{ marginBottom: 11 }}>
                {SEGS.map((s) => <button key={s.id} className={'opt-pill' + (segSel === s.id ? ' on' : '')} style={{ padding: '4px 9px', fontSize: 11 }} onClick={() => setDdSeg((p) => ({ ...p, [m.id]: s.id }))}>{s.icon} {s.name}{s.id === m.targetSeg ? ' ◎' : ''}</button>)}
              </div>
              {concernBar(segObj)}
              {ddRows(b, segObj)}
              <div style={{ marginTop: 11, paddingTop: 10, borderTop: '1px solid var(--card-b)', fontSize: 12.5, lineHeight: 1.7 }}>
                ≈ <b style={{ color: 'var(--acc)' }}>{(b.segShare * 100).toFixed(1)}%</b> of the world segment<br />
                distribution reaches <b style={{ color: b.covReach < 0.4 ? 'var(--warn)' : 'var(--good)' }}>{Math.round(b.covReach * 100)}%</b> of segment buyers — expand in Distribution<br />
                capped by {gvSel.production > 0 ? 'your production volume (' + gvSel.production + 'k/qtr)' : 'remaining sell-through inventory (' + fmtK(gvSel.inventory) + ')'}
              </div>
            </div>
          );
        })}
        {comps.map((comp) => {
          const segSel = ddCompSeg[comp.id] || comp.focus[0];
          const b = Eng.compBreakdown(g, comp, segSel);
          const segObj = SEGS.find((s) => s.id === segSel);
          return (
            <div key={comp.id} style={{ padding: 13, border: '1px solid var(--card-b)', borderRadius: 11, background: 'var(--field)' }}>
              <div className="spread" style={{ marginBottom: 8 }}><b style={{ color: comp.color }}>🛰 {comp.name} {comp.model || ''} {compGenN}</b><span className="sub">{fmt$(b.price)}</span></div>
              <div className="opt-row" style={{ marginBottom: 11 }}>
                {SEGS.map((s) => <button key={s.id} className={'opt-pill' + (segSel === s.id ? ' on' : '')} style={{ padding: '4px 9px', fontSize: 11 }} onClick={() => setDdCompSeg((p) => ({ ...p, [comp.id]: s.id }))}>{s.icon} {s.name}{comp.focus.includes(s.id) ? ' ◎' : ''}</button>)}
              </div>
              {concernBar(segObj)}
              {ddRows(b, segObj)}
              <div style={{ marginTop: 11, paddingTop: 10, borderTop: '1px solid var(--card-b)', fontSize: 12.5, lineHeight: 1.7 }}>
                ≈ <b style={{ color: comp.color }}>{(b.segShare * 100).toFixed(1)}%</b> of the world segment<br />
                brand: loyalty <b>{Math.round(b.loyalty)}</b> · awareness <b>{Math.round(b.aw * 100)}%</b> → Brand Rating <b>{Math.round(b.brandR)}</b><br />
                ◎ = segments this rival focuses on
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  // ── market-share trend ──────────────────────────────────────────────────────────
  const trend = () => {
    const data = (g.history as any[]).map((h) => {
      const row: Record<string, number | string> = { round: 'Q' + h.round, You: Number(((h.share ?? 0) * 100).toFixed(1)) };
      for (const c of comps) { const cs = h.compShare?.[c.id]; if (cs != null) row[c.name] = Number((cs * 100).toFixed(1)); }
      return row;
    });
    return (
      <div className="card">
        <h3>Market share trend</h3>
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={data} margin={{ left: 0, right: 8, top: 4 }}>
            <CartesianGrid stroke="rgb(var(--c-txt) / .08)" vertical={false} />
            <XAxis dataKey="round" stroke="var(--mut)" fontSize={12} />
            <YAxis stroke="var(--mut)" fontSize={12} width={36} />
            <Tooltip contentStyle={TOOLTIP} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Line type="monotone" dataKey="You" stroke="var(--acc)" strokeWidth={3} dot={false} isAnimationActive={!reduceMotion} />
            {comps.map((c) => <Line key={c.id} type="monotone" dataKey={c.name} stroke={c.color} strokeWidth={2} dot={false} isAnimationActive={!reduceMotion} />)}
          </LineChart>
        </ResponsiveContainer>
      </div>
    );
  };

  if (models.length === 0) {
    return (
      <div className="in-root">
        <div className="screen-head"><h2>🕵️ Market Intelligence<WhyChip /></h2></div>
        <EmptyNote>Design a product to see the competitive landscape and demand-driver analysis.</EmptyNote>
      </div>
    );
  }

  return (
    <div className="in-root">
      <div className="screen-head">
        <h2>🕵️ Market Intelligence<WhyChip /></h2>
        <div className="sub">Know your enemy and your customer. Rivals' specs are inferred from their completed research.</div>
      </div>
      {landscape()}
      {forecast()}
      {demandDrivers()}
      {last ? trend() : null}
    </div>
  );
}
